import type LogtoBrowserClient from '@logto/browser'
import { describeAuthFailure, isInvalidGrant } from '@/utils/authRecovery'
import { logger } from '@/utils/eventLogger'

type AccessTokenEntry = { expiresAt: number }

/* Типы SDK прячут refresh и помечают clear* как readonly, хотя на инстансе это обычные методы. */
type TokenClient = {
  accessTokenMap: Map<string, AccessTokenEntry>
  tokenTrace?: boolean
  getRefreshToken: () => Promise<string | null>
  loadAccessTokenMap: () => Promise<void>
  getAccessTokenByRefreshToken: (resource?: string, organizationId?: string) => Promise<string>
  clearAccessToken: () => Promise<void>
  clearAllTokens: () => Promise<void>
}

const TOKEN_NAMES = ['access token', 'refresh token', 'id token'] as const

const LISTENER_FLAG = '__daomedTokenTraceListener'

function tokenName(key: string): string | null {
  if (!key.startsWith('logto:')) return null
  if (key.endsWith(':accessToken')) return 'access token'
  if (key.endsWith(':refreshToken')) return 'refresh token'
  if (key.endsWith(':idToken')) return 'id token'
  return null
}

function storedLeftSec(): number | null {
  const key = Object.keys(localStorage).find(item => item.endsWith(':accessToken'))
  if (!key) return null
  try {
    const map = JSON.parse(localStorage.getItem(key) || '') as Record<string, AccessTokenEntry>
    const deadlines = Object.values(map).map(entry => entry.expiresAt)
    if (!deadlines.length) return null
    return Math.round(Math.max(...deadlines) - Date.now() / 1000)
  } catch {
    return null
  }
}

function memoryLeftSec(client: TokenClient): number | null {
  const map = client.accessTokenMap
  let best: number | null = null
  const now = Date.now() / 1000
  for (const entry of map.values()) {
    const left = Math.round(entry.expiresAt - now)
    if (best === null || left > best) best = left
  }
  return best
}

function lifeText(left: number | null): string {
  if (left === null) return 'access token в браузере нет'
  if (left > 90) return `access token ещё около ${Math.round(left / 60)} мин`
  if (left > 0) return `access token ещё ${left} с`
  return 'срок access token уже вышел'
}

/** Какие из трёх ключей сейчас лежат в localStorage. */
export function describeStoredTokens(): string {
  const present = new Set(
    Object.keys(localStorage)
      .map(tokenName)
      .filter((name): name is string => name !== null),
  )
  const found = TOKEN_NAMES.filter(name => present.has(name))
  const missing = TOKEN_NAMES.filter(name => !present.has(name))
  if (!found.length) return 'В браузере нет ни одного токена.'
  if (!missing.length) return `В браузере все три токена. ${lifeText(storedLeftSec())}.`
  return `В браузере есть: ${found.join(', ')}. Нет: ${missing.join(', ')}. ${lifeText(storedLeftSec())}.`
}

function stackFrames(): string[] {
  return (new Error().stack ?? '')
    .split('\n')
    .map(line => {
      const source = line.match(/\/(src\/[^:?)]+)(?:\?[^:)]*)?:(\d+)/)
      if (source) return `${source[1]}:${source[2]}`
      const sdk = line.match(/\/(node_modules\/\.vite\/deps\/[^:?)]+)(?:\?[^:)]*)?:(\d+)/)
      return sdk ? `${sdk[1]}:${sdk[2]}` : ''
    })
    .filter(frame => frame && !frame.includes('tokenTrace.ts') && !frame.includes('eventLogger.ts'))
}

/** Человеческое описание действия. Пути файлов и номера строк в лог не попадают. */
function humanAction(): string {
  const frames = stackFrames().filter(frame => frame.startsWith('src/'))
  const has = (part: string) => frames.some(frame => frame.includes(part))
  const lineOf = (part: string) => {
    const frame = frames.find(item => item.includes(part))
    return Number(frame?.split(':').pop())
  }
  if (has('UserProfile.vue')) return 'Нажали «Выход» в меню пользователя'
  if (has('login.vue')) {
    return lineOf('login.vue') >= 59
      ? 'Нажали «Сменить аккаунт» на странице входа'
      : 'Нажали «Войти» на странице входа'
  }
  if (has('logto-callback.vue')) return 'Возврат из окна входа'
  if (has('/user/stores/userStore')) return 'Портал загружает профиль'
  if (has('/links/')) return 'Открыт раздел «Полезные ссылки»'
  if (has('/news/')) return 'Открыт раздел «Новости»'
  if (has('authStore')) return 'Портал проверяет или закрывает сессию'
  if (has('axiosInterceptors')) return 'Портал запрашивает данные с сервера'
  return ''
}

let wipingAll = false
let currentAction = ''
let pendingWipe: 'logout' | 'refresh-rejected' | 'session-ended' | null = null

/** Зачем сейчас сотрут все токены. clearAllTokens читает это и пишет нужную фразу. */
export function markTokenWipe(kind: 'logout' | 'refresh-rejected' | 'session-ended'): void {
  pendingWipe = kind
}

function wipeMessage(kind: typeof pendingWipe): string {
  const state = describeStoredTokens()
  if (kind === 'refresh-rejected')
    return `Access token истёк. Обновить его не удалось, refresh token отклонили. Поэтому сессию закрыли и стёрли все токены. ${state}`
  if (kind === 'logout')
    return `Сессию закрыли. Стираем все токены из-за выхода, даже если access token ещё жив. ${state}`
  return `Сессию закрыли и стираем все токены. ${state}`
}

function warn(text: string) {
  logger.warn(`[Токены] ${text}`)
}

/** Подхватить access token, который другая вкладка уже записала в localStorage. */
export async function reloadAccessTokenFromStorage(client: LogtoBrowserClient): Promise<void> {
  const traced = client as unknown as TokenClient
  await traced.loadAccessTokenMap()
  logger.warn(`[Токены] Токен из браузера подхватили, из памяти его не удаляли. ${describeStoredTokens()}`)
}

function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Постоянный след refresh и пропажи ключей.
 * warn уходит и в консоль, и на /api/logger.
 */
export function installTokenTrace(client: LogtoBrowserClient) {
  if (typeof window === 'undefined') return
  const traced = client as unknown as TokenClient

  const storage = localStorage
  const marked = storage.setItem as typeof storage.setItem & { tokenTrace?: boolean }
  if (!marked.tokenTrace) {
    const setItem = storage.setItem.bind(storage)
    const removeItem = storage.removeItem.bind(storage)
    const wrappedSet: typeof storage.setItem & { tokenTrace?: boolean } = (key, value) => {
      const name = tokenName(key)
      setItem(key, value)
      if (!name || wipingAll || currentAction) return
      const action = humanAction()
      warn(`${action ? `${action}. ` : ''}В браузер записан ${name}. ${describeStoredTokens()}`)
    }
    wrappedSet.tokenTrace = true
    storage.setItem = wrappedSet
    storage.removeItem = (key: string) => {
      const name = tokenName(key)
      const left = name === 'access token' ? storedLeftSec() : null
      removeItem(key)
      if (!name || wipingAll || currentAction) return
      if (name === 'access token' && left === null) {
        warn('Команда стереть access token пришла повторно. В браузере его уже не было, удалять нечего.')
        return
      }
      const action = humanAction()
      const before = name === 'access token' ? `До этого ${lifeText(left)}. ` : ''
      warn(`${action ? `${action}. ` : ''}Из браузера убран ${name}. ${before}${describeStoredTokens()}`)
    }
  }

  const windowWithFlag = window as Window & { [LISTENER_FLAG]?: boolean }
  if (!windowWithFlag[LISTENER_FLAG]) {
    windowWithFlag[LISTENER_FLAG] = true
    window.addEventListener('storage', event => {
      const name = event.key ? tokenName(event.key) : null
      if (!name) return
      const action = event.newValue == null ? 'удалила' : 'записала'
      warn(`Другая вкладка ${action} ${name}. ${describeStoredTokens()}`)
    })
  }

  if (traced.tokenTrace) return
  traced.tokenTrace = true

  const refresh = traced.getAccessTokenByRefreshToken.bind(traced)
  traced.getAccessTokenByRefreshToken = (async (resource?: string, organizationId?: string) => {
    const stored = storedLeftSec()
    const memory = memoryLeftSec(traced)
    const action = humanAction() || 'Портал запрашивает данные'
    currentAction = action
    const reason = memory !== null && memory <= 0
      ? `${action}. Срок access token вышел, получаем новый. Это нормально.`
      : stored !== null && stored > 0
        ? `${action}. Нужен access token для сервера. Тот, что уже лежит в браузере, для этого запроса не подходит, хотя ${lifeText(stored)}. Получаем подходящий. Это не ошибка.`
        : `${action}. Access token нигде нет, пытаемся получить новый.`
    warn(reason)
    const refreshTokenBefore = await traced.getRefreshToken()
    try {
      const accessToken = await refresh(resource, organizationId)
      warn(`${action}. Новый access token получен. ${describeStoredTokens()}`)
      return accessToken
    } catch (error) {
      if (isInvalidGrant(error)) {
        let refreshTokenNow = await traced.getRefreshToken()
        if (!refreshTokenNow || refreshTokenNow === refreshTokenBefore) {
          await wait(400)
          refreshTokenNow = await traced.getRefreshToken()
        }
        if (refreshTokenNow && refreshTokenNow !== refreshTokenBefore) {
          warn(`${action}. Этот refresh token уже недействителен, другая вкладка записала новый. Пробуем его и не выходим.`)
          try {
            const accessToken = await refresh(resource, organizationId)
            warn(`${action}. Вторая попытка удалась, сессию не закрываем. ${describeStoredTokens()}`)
            return accessToken
          } catch (retryError) {
            logger.error(
              `[Токены] ${action}. Повторная попытка тоже не удалась: ${describeAuthFailure(retryError)}. ${describeStoredTokens()} Сессию закрываем.`,
            )
            throw retryError
          }
        }
      }
      logger.error(
        `[Токены] ${action}. Получить новый access token не удалось: ${describeAuthFailure(error)}. ${describeStoredTokens()} В браузере тот же refresh token, повторять нечем.`,
      )
      throw error
    } finally {
      currentAction = ''
    }
  }) as TokenClient['getAccessTokenByRefreshToken']

  const clearAccessToken = traced.clearAccessToken.bind(traced)
  traced.clearAccessToken = (async () => {
    if (!wipingAll) {
      const left = storedLeftSec()
      const verdict = left !== null && left > 0
        ? `Стираем access token, хотя ${lifeText(left)}. Следующий запрос получит новый раньше срока.`
        : `Стираем access token. ${lifeText(left)}.`
      warn(`${verdict} ${describeStoredTokens()}`)
    }
    return clearAccessToken()
  }) as TokenClient['clearAccessToken']

  const clearAllTokens = traced.clearAllTokens.bind(traced)
  traced.clearAllTokens = (async () => {
    const kind = pendingWipe
    pendingWipe = null
    warn(wipeMessage(kind))
    wipingAll = true
    try {
      return await clearAllTokens()
    } finally {
      wipingAll = false
    }
  }) as TokenClient['clearAllTokens']
}
